import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { pagesApi } from "../../services/api";
import mediaService from "../../services/mediaService";

function PageForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    page_type: "home",
    meta_title: "",
    meta_description: "",
    hero_headline: "",
    hero_subheadline: "",
    cta_text: "",
    hero_image: "",
    use_slider: false,
    slider_images: [],
    slider_interval: 5000,
    is_active: true,
  });
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState(null);
  const [previewSlides, setPreviewSlides] = useState(0);

  const pageTypes = [
    { value: "home", label: "Home" },
    { value: "about", label: "About" },
    { value: "consulting", label: "Consulting" },
    { value: "coaching", label: "Coaching" },
    { value: "programs", label: "Programs" },
    { value: "corporate-training", label: "Corporate Training" },
    { value: "insights", label: "Insights" },
    { value: "contact", label: "Contact" },
  ];

  useEffect(() => {
    if (id && id !== "new") {
      loadPage();
    }
  }, [id]);

  const loadPage = async () => {
    setLoading(true);
    try {
      const response = await pagesApi.getById(id);
      const data = response.data || {};
      let sliderImages = data.slider_images || [];
      if (typeof sliderImages === "string") {
        try {
          sliderImages = JSON.parse(sliderImages);
        } catch {
          sliderImages = sliderImages
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      }
      setFormData({
        ...data,
        use_slider: !!data.use_slider,
        slider_images: Array.isArray(sliderImages) ? sliderImages : [],
        slider_interval: data.slider_interval || 5000,
      });
    } catch (error) {
      console.error("Error loading page:", error);
      alert("Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        slider_images: Array.isArray(formData.slider_images)
          ? formData.slider_images
          : [],
        slider_interval: Number(formData.slider_interval) || 5000,
      };
      if (id && id !== "new") {
        await pagesApi.update(id, payload);
        alert("Page updated successfully!");
      } else {
        await pagesApi.create(payload);
        alert("Page created successfully!");
      }
      navigate("/admin/pages");
    } catch (error) {
      console.error("Error saving page:", error);
      alert(
        `Failed to save page: ${error.response?.data?.detail || error.message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-");
    setFormData((prev) => ({ ...prev, slug }));
  };

  const loadMediaLibrary = async () => {
    try {
      const media = await mediaService.getMediaList("hero");
      setMediaLibrary(Array.isArray(media) ? media : []);
      setShowMediaLibrary(true);
    } catch (error) {
      console.error("Error loading media library:", error);
      alert("Failed to load media library");
    }
  };

  const handleSelectImage = (image) => {
    const selectedPath = image.path || image.url || image.file_path || "";

    if (selectedImageType === "background") {
      setFormData((prev) => ({
        ...prev,
        hero_image: selectedPath,
      }));
    } else if (selectedImageType === "slider") {
      setFormData((prev) => ({
        ...prev,
        slider_images: prev.slider_images.includes(selectedPath)
          ? prev.slider_images
          : [...prev.slider_images, selectedPath],
      }));
    }

    setShowMediaLibrary(false);
    setSelectedImageType(null);
  };

  const removeSliderImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      slider_images: prev.slider_images.filter((_, i) => i !== index),
    }));
  };

  const getPreviewUrl = (path) => {
    if (!path) return "";
    return path.startsWith("http") ? path : mediaService.getFileUrl(path);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {id && id !== "new" ? "Edit Page" : "Create New Page"}
          </h1>
          <p className="text-gray-600">Manage your website pages</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Basic Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={generateSlug}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page Type *
              </label>
              <select
                name="page_type"
                value={formData.page_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {pageTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Hero Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Hero Section</h2>

            {/* Text Content */}
            <div className="space-y-4 pb-6 border-b">
              <h3 className="font-medium text-gray-900">Text Content</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hero Headline
                </label>
                <input
                  type="text"
                  name="hero_headline"
                  value={formData.hero_headline || ""}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter hero headline"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hero Subheadline
                </label>
                <textarea
                  name="hero_subheadline"
                  value={formData.hero_subheadline || ""}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter hero subheadline"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CTA Text
                </label>
                <input
                  type="text"
                  name="cta_text"
                  value={formData.cta_text || ""}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter call-to-action text"
                />
              </div>
            </div>

            {/* Background Image */}
            <div className="space-y-4 pb-6 border-b">
              <h3 className="font-medium text-gray-900">Background Image</h3>

              {formData.hero_image && (
                <div className="relative">
                  <img
                    src={getPreviewUrl(formData.hero_image)}
                    alt="Background preview"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, hero_image: "" }))
                    }
                    className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setSelectedImageType("background");
                  loadMediaLibrary();
                }}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition"
              >
                {formData.hero_image
                  ? "Change Background Image"
                  : "Select Background Image"}
              </button>
            </div>

            {/* Slider Settings */}
            <div className="space-y-4 pb-6 border-b">
              <h3 className="font-medium text-gray-900">Image Slider</h3>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="use_slider"
                  name="use_slider"
                  checked={!!formData.use_slider}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="use_slider"
                  className="ml-3 text-sm font-medium text-gray-700"
                >
                  Enable image slider in hero section
                </label>
              </div>

              {formData.use_slider && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slider Interval (milliseconds)
                  </label>
                  <input
                    type="number"
                    name="slider_interval"
                    value={formData.slider_interval || 5000}
                    onChange={handleChange}
                    min="1000"
                    step="1000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Time between slides in milliseconds
                  </p>
                </div>
              )}

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Slider Images ({formData.slider_images?.length || 0})
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedImageType("slider");
                      loadMediaLibrary();
                    }}
                    disabled={!formData.use_slider}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    + Add Image
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.slider_images?.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={getPreviewUrl(image)}
                        alt={`Slider ${index + 1}`}
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeSliderImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      >
                        ✕
                      </button>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        Slide {index + 1}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Page Content</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content (HTML)
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="Enter HTML content here..."
              />
            </div>
          </div>

          {/* SEO Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">SEO Settings</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Title
              </label>
              <input
                type="text"
                name="meta_title"
                value={formData.meta_title || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Home - Innobig"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Description
              </label>
              <textarea
                name="meta_description"
                value={formData.meta_description || ""}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Welcome to Innobig - Empowering growth through innovation"
              />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Status</h2>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor="is_active"
                className="ml-2 block text-sm text-gray-900"
              >
                Active (Show this page on the website)
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate("/admin/pages")}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving
                ? "Saving..."
                : id && id !== "new"
                  ? "Update Page"
                  : "Create Page"}
            </button>
          </div>

          {/* Preview */}
          <div className="mt-8 pt-6 border-t">
            <h3 className="font-semibold mb-4">Preview</h3>
            <div
              className="relative bg-gradient-to-r from-[#1F3A5F] to-[#3B82F6] text-white p-12 rounded-lg overflow-hidden"
              style={{
                backgroundImage: formData.hero_image
                  ? `url(${getPreviewUrl(formData.hero_image)})`
                  : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {formData.hero_image && (
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
              )}

              <div className="relative z-10">
                <h1 className="text-3xl font-bold mb-3">
                  {formData.hero_headline || "Headline"}
                </h1>
                <p className="text-lg mb-6 max-w-2xl">
                  {formData.hero_subheadline || "Subheadline"}
                </p>
                <button
                  type="button"
                  className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition"
                >
                  {formData.cta_text || "Call to Action"}
                </button>
              </div>

              {formData.use_slider && formData.slider_images?.length > 0 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                  {formData.slider_images.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setPreviewSlides(index)}
                      className={`w-2 h-2 rounded-full transition ${
                        previewSlides === index
                          ? "bg-white"
                          : "bg-white bg-opacity-50"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="font-semibold">Select Image</h3>
              <button
                onClick={() => setShowMediaLibrary(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="p-4">
              {mediaLibrary.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No media files found
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {mediaLibrary.map((media, index) => {
                    const fullUrl = mediaService.getFileUrl(
                      media.path || media.url || media.file_path,
                    );
                    return (
                      <button
                        key={index}
                        onClick={() => handleSelectImage(media)}
                        className="relative group"
                      >
                        <img
                          src={fullUrl}
                          alt={media.name || media.filename}
                          className="w-full aspect-square object-cover rounded-lg border-2 border-transparent group-hover:border-blue-500 transition"
                        />
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          {media.name || media.filename}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PageForm;
